import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
} from '@angular/core';
import { CONFIGURATION_SETTING_KEYS, ISubscriptionTicket } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../../configuration/configuration-store.service';
import { ActiveSubscriptionGateway } from './active-subscriptions.gateway';

interface IActiveSubscriptionRequest {
  serviceAreaId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ActiveSubscriptionTicketsStore {
  private readonly gateway: ActiveSubscriptionGateway =
    new ActiveSubscriptionGateway();
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly subscriptionServiceArea = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.SubscriptionServiceArea
    )
  );

  private readonly subscriptionRequest: Signal<IActiveSubscriptionRequest> =
    computed(
      (): IActiveSubscriptionRequest => ({
        serviceAreaId: this.subscriptionServiceArea(),
      })
    );

  #subscriptionResource: ResourceRef<
    IApiResponse<ISubscriptionTicket[]> | undefined
  > = resource({
    params: this.subscriptionRequest,
    loader: ({ params }: ResourceLoaderParams<IActiveSubscriptionRequest>) => {
      return params != undefined
        ? firstValueFrom(this.gateway.list(params.serviceAreaId))
        : Promise.resolve(undefined);
    },
  });

  readonly subscriptions: Signal<ISubscriptionTicket[]> = computed(
    (): ISubscriptionTicket[] =>
      this.#subscriptionResource.hasValue()
        ? (this.#subscriptionResource.value()?.data ?? [])
        : []
  );

  readonly isLoading: Signal<boolean> = computed(() =>
    this.#subscriptionResource.isLoading()
  );

  public reloadSubscriptionTickets(): void {
    this.#subscriptionResource.reload();
  }
}
